import { useMutation } from '@tanstack/react-query';
import { updateOnboarding } from '../../clients/onboardingClient';
import { validateRedirectUrl } from '../../common/utils/utils';
import type { Step, StepRuntime } from '../types/types';
import { StepSlug } from '../types/types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function FeatureStep(_runtime: StepRuntime): JSX.Element {
  const featureStepMutation = useMutation({
    mutationFn: updateOnboarding,
    onSettled: () => {
      // Onboarding complete, check for redirect in sessionStorage
      const redirect = sessionStorage.getItem('postAuthRedirect');
      // Validate redirect against whitelist
      const validRedirect = validateRedirectUrl(redirect);
      // Clear sessionStorage after reading
      if (redirect) {
        sessionStorage.removeItem('postAuthRedirect');
      }

      // Navigate to redirect URL or home
      setTimeout(() => {
        window.location.href = validRedirect || '/';
      }, 100);
    }
  });

  function handleContinue() {
    featureStepMutation.mutate({
      progress: 3
    });
  }
  return (
    <div className="h-screen w-full bg-[url('./images/grid.png')] bg-repeat bg-[length:500px] text-slate-900 flex flex-col">
      {/* Split pane - full height and width */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 h-full mx-auto">
        {/* Left column - full height and width */}
        <div className="h-full w-full flex flex-col justify-center px-6 sm:px-10 lg:px-12">
          <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-semibold tracking-tight mb-8">
            Focus on people, not paperwork
          </h1>

          <div className="max-w-2xl lg:max-w-lg">
            <ul className="space-y-8 text-[15px] leading-7">
              <li className="flex items-start gap-4">
                <div>
                  <p className="font-semibold mb-1">✨ Save hours with AI-powered documentation</p>
                  <p className="text-slate-600">
                    Automatically turn client conversations into case notes, intake forms, and
                    follow-up templates. Free up time to focus on your clients, not paperwork.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div>
                  <p className="font-semibold mb-1">📂 Stay organized across cases and clients</p>
                  <p className="text-slate-600">
                    Keep all your meeting notes, forms, and documents in one secure place. Easily
                    search past conversations and share what matters with your team.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div>
                  <p className="font-semibold mb-1">🔒 Built for compliance and privacy</p>
                  <p className="text-slate-600">
                    Stay aligned with HIPAA and other regulatory requirements. All notes are
                    encrypted and stored securely so you can work with confidence.
                  </p>
                </div>
              </li>
            </ul>

            {/* Continue button */}
            <div className="mt-12">
              <button
                type="button"
                className="text-sm text-indigo-600 font-semibold hover:opacity-100 transition opacity-70"
                onClick={handleContinue}
              >
                Continue to Notewell ››
              </button>
            </div>
          </div>
        </div>

        {/* Right column mock - full height and width, hidden on mobile */}
        <div className="hidden lg:flex h-full w-full items-center justify-center px-6 sm:px-10 lg:px-14">
          <div className="relative w-full max-w-[575px]">
            <div className="absolute -inset-10 rounded-[32px] bg-gradient-to-b from-pink-100/60 via-white to-blue-100/60 blur-0" />
            <div className="relative w-full rounded-3xl bg-white shadow-xl ring-1 ring-black/5 overflow-hidden">
              <div className="grid grid-cols-[150px_1fr]">
                {/* Sidebar */}
                <aside className="bg-white border-r border-slate-200 p-4 flex flex-col">
                  <div className="flex items-center justify-center mb-6 mt-6">
                    <img src="/logo_horizontal.png" alt="NoteWell" className="h-6 w-auto" />
                  </div>

                  <nav className="space-y-1 text-sm flex-1">
                    <NavItem label="Home" iconType="home" active={false} />
                    <NavItem label="Clients" iconType="user" active={false} />
                    <NavItem label="Notes" iconType="calendar" active={true} />
                    <NavItem label="Templates" iconType="document" active={false} />
                    <NavItem label="AI Chat" iconType="chat" active={false} />
                  </nav>

                  <div className="mt-auto pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-2 px-2 py-2">
                      <div className="h-6 w-6 rounded-full bg-slate-300" />
                      <span className="text-xs font-medium text-slate-700">Demo User</span>
                    </div>
                  </div>
                </aside>

                {/* Main content */}
                <main className="bg-white">
                  <div className="px-6 pt-9 pb-4 border-b border-slate-200">
                    <h2 className="text-[18px] font-semibold mb-4">
                      Check-in Meeting with Jane Doe
                    </h2>

                    {/* Basic Meeting Data */}
                    <div className="space-y-2 text-[13px] mb-4">
                      {/* <MeetingDataRow label="Worker" value="Demo User" /> */}
                      <MeetingDataRow label="Client" value="Jane Doe" isLink />
                      <MeetingDataRow label="Template" value="Goal-Setting Meeting" isLink />
                    </div>

                    <div className="mt-4 flex gap-6 text-sm">
                      <button className="relative pb-2 font-medium text-slate-900 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-slate-900">
                        Meeting Notes
                      </button>
                      <button className="pb-2 text-slate-500">Transcript</button>
                    </div>
                  </div>

                  <div className="px-6 py-5 space-y-6 text-[14px] leading-6">
                    {/* Paragraph Question */}
                    <div className="space-y-2">
                      <p className="font-medium text-slate-700">
                        What's the client's current situation, and are there any recent changes
                        affecting their goals?
                      </p>
                      <div className="text-[13px] text-slate-600 border-b border-slate-300 pb-2">
                        The client recently exited an abusive relationship and has been dealing with
                        the complexities of keeping her children in school and managing housing
                        issues. She is experiencing significant stress and instability currently
                        impacting her goals.
                      </div>
                    </div>

                    {/* Short Answer List Question */}
                    <div className="space-y-2 pb-4">
                      <p className="font-medium text-slate-700">
                        What are the top priorities in the client's life that they want to focus on?
                      </p>
                      <ol className="list-[lower-alpha] ml-5 space-y-2 text-[13px]">
                        <li>
                          <div className="text-slate-600 border-b border-slate-300 pb-1">
                            Mental health
                          </div>
                        </li>
                        <li>
                          <div className="text-slate-600 border-b border-slate-300 pb-1">
                            Stable housing
                          </div>
                        </li>
                        <li>
                          <div className="text-slate-600 border-b border-slate-300 pb-1">
                            Childrens' well-being
                          </div>
                        </li>
                      </ol>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavItem({
  label,
  iconType,
  active
}: {
  label: string;
  iconType: string;
  active?: boolean;
}) {
  const iconColor = active ? 'text-indigo-600' : 'text-slate-400';

  return (
    <div
      className={
        'flex items-center gap-3 px-2 py-2 rounded-md text-sm font-semibold ' +
        (active
          ? 'bg-indigo-50 text-indigo-800'
          : 'text-slate-700 hover:bg-indigo-50 hover:text-indigo-600')
      }
    >
      <svg
        className={`h-5 w-5 flex-shrink-0 ${iconColor}`}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
      >
        {iconType === 'home' && (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
          />
        )}
        {iconType === 'user' && (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
          />
        )}
        {iconType === 'calendar' && (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
          />
        )}
        {iconType === 'document' && (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
          />
        )}
        {iconType === 'chat' && (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.068.157 2.148.279 3.238.364.466.037.893.281 1.153.671L12 21l2.652-3.978c.26-.39.687-.634 1.153-.67 1.09-.086 2.17-.208 3.238-.365 1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
          />
        )}
      </svg>
      <span className="truncate">{label}</span>
    </div>
  );
}

function MeetingDataRow({
  label,
  value,
  isLink
}: {
  label: string;
  value: string;
  isLink?: boolean;
}) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-medium text-slate-600 w-24">{label}</span>
      {isLink ? (
        <span className="text-indigo-700 bg-indigo-50/70 px-1.5 py-0.5 border-b border-indigo-300">
          {value}
        </span>
      ) : (
        <span className="text-slate-700">{value}</span>
      )}
    </div>
  );
}

export const FeatureStepModule: Step = {
  slug: StepSlug.Features,
  title: 'Features',
  Component: FeatureStep
};
